import { useState } from 'react'
import {
  Box,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import PageHeader from '../../Shared/PageHeader'
import ReportPanel, { ValorOuIndisponivel } from './ReportPanel'
import {
  getHistoryReport,
  getOperatorReport,
  getProductionReport,
  getRfidReport,
} from '../../../services/reportServices'
import {
  formatDate,
  formatDateTime,
  formatDuration,
  formatNumber,
  formatPercent,
} from '../../../utils/format'

/**
 * Os quatro relatórios do Épico 09, consumindo `/reports/*`.
 *
 * Os valores dos filtros são os do banco novo (`CONCLUIDA`, `EM_PRODUCAO`, …),
 * não os inteiros do legado: o status agora é texto com CHECK no schema, e
 * traduzir de um vocabulário para o outro na tela só recriaria a confusão que a
 * migração desfez. O rótulo em português vem do campo `*Label` da API.
 */

const PRODUCTION_STATUS_COLOR = {
  RASCUNHO: 'default',
  ABERTA: 'info',
  EM_ANDAMENTO: 'warning',
  CONCLUIDA: 'success',
  CANCELADA: 'error',
}

const RFID_SITUATION_COLOR = {
  CRIADO: 'default',
  IMPRESSO: 'info',
  AGUARDANDO_PRODUCAO: 'warning',
  EM_PRODUCAO: 'warning',
  EM_CONFERENCIA: 'warning',
  FINALIZADO: 'success',
  EXPEDIDO: 'success',
  CANCELADO: 'error',
  PERDIDO: 'error',
  BLOQUEADO: 'error',
}

const PRODUCTION_STATUS_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'RASCUNHO', label: 'Rascunho' },
  { value: 'ABERTA', label: 'Aberta' },
  { value: 'EM_ANDAMENTO', label: 'Em andamento' },
  { value: 'CONCLUIDA', label: 'Concluída' },
  { value: 'CANCELADA', label: 'Cancelada' },
]

const RFID_SITUATION_OPTIONS = [
  { value: '', label: 'Todas' },
  { value: 'CRIADO', label: 'Criado' },
  { value: 'IMPRESSO', label: 'Impresso' },
  { value: 'AGUARDANDO_PRODUCAO', label: 'Aguardando produção' },
  { value: 'EM_PRODUCAO', label: 'Em produção' },
  { value: 'EM_CONFERENCIA', label: 'Em conferência' },
  { value: 'FINALIZADO', label: 'Finalizado' },
  { value: 'EXPEDIDO', label: 'Expedido' },
  { value: 'CANCELADO', label: 'Cancelado' },
  { value: 'PERDIDO', label: 'Perdido' },
  { value: 'BLOQUEADO', label: 'Bloqueado' },
]

const HISTORY_SOURCE_OPTIONS = [
  { value: '', label: 'Todas' },
  { value: 'APONTAMENTO', label: 'Apontamento de etapa' },
  { value: 'EVENTO_RFID', label: 'Evento da etiqueta' },
  { value: 'IMPRESSAO', label: 'Impressão' },
]

const HISTORY_SOURCE_LABEL = HISTORY_SOURCE_OPTIONS.reduce(
  (mapa, item) => ({ ...mapa, [item.value]: item.label }),
  {}
)

/** Barra de eficiência que não desenha barra nenhuma quando não há eficiência. */
const EfficiencyCell = ({ value, motivo }) => {
  if (value === null || value === undefined) {
    return <ValorOuIndisponivel value={null} motivo={motivo} />
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
      <LinearProgress
        variant="determinate"
        value={Math.min(100, Math.max(0, Number(value)))}
        sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
      />
      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
        {formatPercent(value)}
      </Typography>
    </Box>
  )
}

const SelectFilter = ({ label, value, options, onChange }) => (
  <Grid item xs={12} sm={6} md={3}>
    <FormControl fullWidth>
      <InputLabel id={`filter-${label}`}>{label}</InputLabel>
      <Select
        labelId={`filter-${label}`}
        label={label}
        value={value || ''}
        onChange={event => onChange(event.target.value)}
      >
        {options.map(option => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </Grid>
)

const TextFilter = ({ label, value, onChange }) => (
  <Grid item xs={12} sm={6} md={3}>
    <TextField
      label={label}
      value={value || ''}
      onChange={event => onChange(event.target.value)}
      fullWidth
    />
  </Grid>
)

/** Texto que pode faltar na linha: "—" cru, sem inventar preenchimento. */
const texto = value => value || '—'

/**
 * Nome do usuário quando o SSO conhece; o próprio ID quando não conhece mais;
 * `null` quando não há usuário na linha — aí a coluna mostra o motivo em vez de
 * um traço mudo.
 */
const nomeOuId = (nomeDe, nome, id) => nome || (id ? nomeDe(id) : null)

/* ------------------------------------------------------------------ produção -- */

const productionColumns = (motivos, nomeDe) => [
  { id: 'orderNumber', label: 'Ordem', element: row => row.orderNumber },
  { id: 'productName', label: 'Produto', element: row => texto(row.productName) },
  {
    id: 'quantityPlanned',
    label: 'Planejado',
    align: 'right',
    element: row => formatNumber(row.quantityPlanned),
  },
  {
    id: 'quantityProduced',
    label: 'Produzido',
    align: 'right',
    element: row => formatNumber(row.quantityProduced),
  },
  {
    id: 'efficiency',
    label: 'Eficiência',
    element: row => <EfficiencyCell value={row.efficiency} motivo={motivos.efficiency} />,
  },
  {
    id: 'averageMinutes',
    label: 'Tempo médio',
    element: row => (
      <ValorOuIndisponivel
        value={row.averageMinutes}
        motivo={motivos.averageMinutes}
        format={formatDuration}
      />
    ),
  },
  {
    id: 'status',
    label: 'Status',
    element: row => (
      <Chip
        size="small"
        label={row.statusLabel}
        color={PRODUCTION_STATUS_COLOR[row.status] || 'default'}
      />
    ),
  },
  {
    id: 'responsible',
    label: 'Responsável',
    element: row =>
      texto(nomeOuId(nomeDe, row.responsibleName, row.responsibleUserId || row.createdByUserId)),
  },
  { id: 'createdAt', label: 'Criada', element: row => formatDate(row.createdAt) },
  { id: 'finishedAt', label: 'Concluída', element: row => formatDate(row.finishedAt) },
]

const productionExportColumns = nomeDe => [
  { header: 'Ordem', accessor: 'orderNumber' },
  { header: 'Produto', accessor: row => texto(row.productName) },
  { header: 'Planejado', accessor: row => formatNumber(row.quantityPlanned) },
  { header: 'Produzido', accessor: row => formatNumber(row.quantityProduced) },
  { header: 'Eficiência', accessor: row => formatPercent(row.efficiency) },
  { header: 'Tempo médio', accessor: row => formatDuration(row.averageMinutes) },
  { header: 'Status', accessor: 'statusLabel' },
  {
    header: 'Responsável',
    accessor: row =>
      texto(nomeOuId(nomeDe, row.responsibleName, row.responsibleUserId || row.createdByUserId)),
  },
  { header: 'Criada', accessor: row => formatDate(row.createdAt) },
  { header: 'Concluída', accessor: row => formatDate(row.finishedAt) },
]

const productionSummary = [
  { id: 'orders', label: 'Ordens', value: s => s.orders, format: formatNumber },
  { id: 'breaks', label: 'Quebras', value: s => s.breaks, format: formatNumber },
  {
    id: 'quantityPlanned',
    label: 'Planejado',
    value: s => s.quantityPlanned,
    format: formatNumber,
  },
  {
    id: 'quantityProduced',
    label: 'Produzido',
    value: s => s.quantityProduced,
    format: formatNumber,
  },
  { id: 'efficiency', label: 'Eficiência', value: s => s.efficiency, format: formatPercent },
  {
    id: 'averageMinutes',
    label: 'Tempo médio',
    value: s => s.averageMinutes,
    format: formatDuration,
  },
]

/* ---------------------------------------------------------------------- RFID -- */

const rfidColumns = motivos => [
  { id: 'epc', label: 'EPC', element: row => row.epc },
  { id: 'productName', label: 'Produto', element: row => texto(row.productName) },
  {
    id: 'orderNumber',
    label: 'Ordem',
    element: row => <ValorOuIndisponivel value={row.orderNumber} motivo={motivos.orderNumber} />,
  },
  {
    id: 'situation',
    label: 'Situação',
    element: row => (
      <Chip
        size="small"
        label={row.situationLabel}
        color={RFID_SITUATION_COLOR[row.situation] || 'default'}
      />
    ),
  },
  { id: 'stageName', label: 'Etapa', element: row => texto(row.stageName) },
  { id: 'location', label: 'Local', element: row => texto(row.location) },
  { id: 'createdAt', label: 'Criado', element: row => formatDateTime(row.createdAt) },
  {
    id: 'printedAt',
    label: 'Impresso',
    element: row => (
      <ValorOuIndisponivel
        value={row.printedAt}
        motivo={motivos.printed}
        format={formatDateTime}
      />
    ),
  },
  { id: 'lastReadAt', label: 'Última leitura', element: row => formatDateTime(row.lastReadAt) },
]

const rfidExportColumns = [
  { header: 'EPC', accessor: 'epc' },
  { header: 'Produto', accessor: row => texto(row.productName) },
  { header: 'Ordem', accessor: row => texto(row.orderNumber) },
  { header: 'Situação', accessor: 'situationLabel' },
  { header: 'Etapa', accessor: row => texto(row.stageName) },
  { header: 'Local', accessor: row => texto(row.location) },
  { header: 'Criado', accessor: row => formatDateTime(row.createdAt) },
  { header: 'Impresso', accessor: row => formatDateTime(row.printedAt) },
  { header: 'Última leitura', accessor: row => formatDateTime(row.lastReadAt) },
]

const rfidSummary = [
  { id: 'created', label: 'Criadas', value: s => s.created, format: formatNumber },
  { id: 'printed', label: 'Impressas', value: s => s.printed, format: formatNumber },
  { id: 'pending', label: 'Pendentes', value: s => s.pending, format: formatNumber },
  { id: 'produced', label: 'Produzidas', value: s => s.produced, format: formatNumber },
  { id: 'cancelled', label: 'Canceladas', value: s => s.cancelled, format: formatNumber },
  { id: 'lost', label: 'Perdidas', value: s => s.lost, format: formatNumber },
]

/* ------------------------------------------------------------------ operador -- */

const operatorColumns = (motivos, nomeDe) => [
  {
    id: 'operator',
    label: 'Operador',
    element: row => (
      <ValorOuIndisponivel
        value={nomeOuId(nomeDe, row.operatorName, row.operatorId)}
        motivo={motivos.operatorName}
      />
    ),
  },
  { id: 'stageName', label: 'Etapa', element: row => texto(row.stageName) },
  {
    id: 'stagePassages',
    label: 'Apontamentos',
    align: 'right',
    element: row => formatNumber(row.stagePassages),
  },
  { id: 'orders', label: 'Ordens', align: 'right', element: row => formatNumber(row.orders) },
  { id: 'firstAt', label: 'Início', element: row => formatDateTime(row.firstAt) },
  { id: 'lastAt', label: 'Término', element: row => formatDateTime(row.lastAt) },
  {
    id: 'totalMinutes',
    label: 'Tempo gasto',
    element: row => (
      <ValorOuIndisponivel
        value={row.totalMinutes}
        motivo={motivos.totalMinutes}
        format={formatDuration}
      />
    ),
  },
]

const operatorExportColumns = nomeDe => [
  {
    header: 'Operador',
    accessor: row => texto(nomeOuId(nomeDe, row.operatorName, row.operatorId)),
  },
  { header: 'Etapa', accessor: row => texto(row.stageName) },
  { header: 'Apontamentos', accessor: row => formatNumber(row.stagePassages) },
  { header: 'Ordens', accessor: row => formatNumber(row.orders) },
  { header: 'Início', accessor: row => formatDateTime(row.firstAt) },
  { header: 'Término', accessor: row => formatDateTime(row.lastAt) },
  { header: 'Tempo gasto', accessor: row => formatDuration(row.totalMinutes) },
]

const operatorSummary = [
  { id: 'operators', label: 'Operadores', value: s => s.operators, format: formatNumber },
  {
    id: 'stagePassages',
    label: 'Apontamentos',
    value: s => s.stagePassages,
    format: formatNumber,
  },
  { id: 'items', label: 'Etiquetas', value: s => s.items, format: formatNumber },
  { id: 'totalMinutes', label: 'Tempo total', value: s => s.totalMinutes, format: formatDuration },
  {
    id: 'averageMinutes',
    label: 'Tempo médio',
    value: s => s.averageMinutes,
    format: formatDuration,
  },
]

/* ----------------------------------------------------------------- histórico -- */

const historyColumns = (motivos, nomeDe) => [
  { id: 'occurredAt', label: 'Data', element: row => formatDateTime(row.occurredAt) },
  { id: 'epc', label: 'EPC', element: row => texto(row.epc) },
  { id: 'eventType', label: 'Evento', element: row => row.eventType },
  {
    id: 'source',
    label: 'Origem',
    element: row => (
      <Chip
        size="small"
        variant="outlined"
        label={HISTORY_SOURCE_LABEL[row.source] || row.source}
      />
    ),
  },
  { id: 'stageName', label: 'Etapa', element: row => texto(row.stageName) },
  { id: 'orderNumber', label: 'Ordem', element: row => texto(row.orderNumber) },
  {
    id: 'operator',
    label: 'Operador',
    element: row => texto(nomeOuId(nomeDe, row.operatorName, row.operatorId)),
  },
  { id: 'equipmentId', label: 'Equipamento', element: row => texto(row.equipmentId) },
  { id: 'location', label: 'Local', element: row => texto(row.location) },
]

const historyExportColumns = nomeDe => [
  { header: 'Data', accessor: row => formatDateTime(row.occurredAt) },
  { header: 'EPC', accessor: row => texto(row.epc) },
  { header: 'Evento', accessor: 'eventType' },
  { header: 'Origem', accessor: row => HISTORY_SOURCE_LABEL[row.source] || row.source },
  { header: 'Etapa', accessor: row => texto(row.stageName) },
  { header: 'Ordem', accessor: row => texto(row.orderNumber) },
  {
    header: 'Operador',
    accessor: row => texto(nomeOuId(nomeDe, row.operatorName, row.operatorId)),
  },
  { header: 'Equipamento', accessor: row => texto(row.equipmentId) },
  { header: 'Local', accessor: row => texto(row.location) },
]

const historySummary = [
  { id: 'total', label: 'Registros', value: s => s.total, format: formatNumber },
  {
    id: 'stageEvents',
    label: 'Apontamentos',
    value: s => s.stageEvents,
    format: formatNumber,
  },
  {
    id: 'rfidEvents',
    label: 'Eventos da etiqueta',
    value: s => s.rfidEvents,
    format: formatNumber,
  },
  { id: 'printEvents', label: 'Impressões', value: s => s.printEvents, format: formatNumber },
]

/* ---------------------------------------------------------------------- tela -- */

// Fora do componente: identidade estável, senão a busca de nomes reiniciaria a
// cada render do painel.
// Responsável atribuído quando existir; senão quem criou a ordem — os dois são
// ID de usuário e precisam ser resolvidos pelo SSO.
const CAMPOS_RESPONSAVEL = ['responsibleUserId', 'createdByUserId']
const CAMPOS_OPERADOR = ['operatorId']

const PRODUCTION_INITIAL = { orderStatus: '', search: '' }
const RFID_INITIAL = { situation: '', epc: '' }
const OPERATOR_INITIAL = { operator: '', stage: '' }
const HISTORY_INITIAL = { epc: '', source: '' }

const Reports = () => {
  const [tab, setTab] = useState(0)

  return (
    <Box>
      <PageHeader title="Relatórios" subtitle="Consulte, filtre e exporte os dados da produção" />

      <Tabs
        value={tab}
        onChange={(event, value) => setTab(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
      >
        <Tab label="Produção" />
        <Tab label="RFIDs" />
        <Tab label="Por Operador" />
        <Tab label="Histórico" />
      </Tabs>

      {tab === 0 && (
        <ReportPanel
          title="Relatório de Produção"
          description="Ordens do período, com quantidades, eficiência e tempo médio"
          columns={productionColumns}
          exportColumns={productionExportColumns}
          summaryFields={productionSummary}
          camposDeUsuario={CAMPOS_RESPONSAVEL}
          fetcher={getProductionReport}
          initialExtraValues={PRODUCTION_INITIAL}
          extraFilters={(values, setValue) => (
            <>
              <SelectFilter
                label="Status"
                value={values.orderStatus}
                options={PRODUCTION_STATUS_OPTIONS}
                onChange={value => setValue('orderStatus', value)}
              />
              <TextFilter
                label="Ordem ou produto"
                value={values.search}
                onChange={value => setValue('search', value)}
              />
            </>
          )}
        />
      )}

      {tab === 1 && (
        <ReportPanel
          title="Relatório de RFIDs"
          description="Situação das etiquetas por produto, ordem e etapa"
          columns={rfidColumns}
          exportColumns={rfidExportColumns}
          summaryFields={rfidSummary}
          fetcher={getRfidReport}
          initialExtraValues={RFID_INITIAL}
          extraFilters={(values, setValue) => (
            <>
              <SelectFilter
                label="Situação"
                value={values.situation}
                options={RFID_SITUATION_OPTIONS}
                onChange={value => setValue('situation', value)}
              />
              <TextFilter
                label="EPC"
                value={values.epc}
                onChange={value => setValue('epc', value)}
              />
            </>
          )}
        />
      )}

      {tab === 2 && (
        <ReportPanel
          title="Relatório por Operador"
          description="Produção apontada por operador e etapa"
          columns={operatorColumns}
          exportColumns={operatorExportColumns}
          summaryFields={operatorSummary}
          camposDeUsuario={CAMPOS_OPERADOR}
          fetcher={getOperatorReport}
          initialExtraValues={OPERATOR_INITIAL}
          extraFilters={(values, setValue) => (
            <>
              <TextFilter
                label="Operador"
                value={values.operator}
                onChange={value => setValue('operator', value)}
              />
              <TextFilter
                label="Etapa"
                value={values.stage}
                onChange={value => setValue('stage', value)}
              />
            </>
          )}
        />
      )}

      {tab === 3 && (
        <ReportPanel
          title="Relatório de Histórico"
          description="Movimentação registrada das etiquetas: apontamento, evento e impressão"
          columns={historyColumns}
          exportColumns={historyExportColumns}
          summaryFields={historySummary}
          camposDeUsuario={CAMPOS_OPERADOR}
          fetcher={getHistoryReport}
          initialExtraValues={HISTORY_INITIAL}
          extraFilters={(values, setValue) => (
            <>
              <TextFilter
                label="EPC"
                value={values.epc}
                onChange={value => setValue('epc', value)}
              />
              <SelectFilter
                label="Origem"
                value={values.source}
                options={HISTORY_SOURCE_OPTIONS}
                onChange={value => setValue('source', value)}
              />
            </>
          )}
        />
      )}
    </Box>
  )
}

export default Reports
